import { t } from "@lingui/macro";
import cx from "classnames";
import React, { useCallback, useState } from "react";
import { useMedia } from "react-use";

import SearchIcon from "assets/icons/SearchIcon";
import { useOutsideClick } from "lib/useOutsideClick";

import CrossIconComponent from "img/cross.svg?react";
import SearchIconComponent from "img/search.svg?react";

type Props = {
  value: string;
  setValue: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  className?: string;
  placeholder?: string;
  size?: "s" | "m";
  /**
   * If not provided, will be set to false on small screens
   */
  autoFocus?: boolean;
  qa?: string;
};

export default function SearchInput({
  value,
  setValue,
  onKeyDown,
  className,
  placeholder,
  autoFocus,
  size = "m",
  qa = "token-search-input",
}: Props) {
  const isSmallerScreen = useMedia("(max-width: 700px)");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, [setIsFocused]);

  useOutsideClick(containerRef, handleBlur);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    },
    [setValue]
  );

  const handleClear = useCallback(() => {
    setValue("");
    inputRef.current?.focus();
  }, [setValue]);

  const handleClick = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  return (
    <div className={cx("search-input relative flex cursor-pointer items-center p-0", className)} ref={containerRef}>
      <div className="search-input__icon absolute top-0 flex h-full items-center px-8">
        <SearchIcon
          height={20}
          width={20}
          onClick={handleClick}
          className={cx("relative -top-1 ", {
            "text-white-alpha-16": !isFocused,
            "text-white": isFocused,
          })}
        />
      </div>
      <input
        ref={inputRef}
        data-qa={qa}
        type="text"
        placeholder={placeholder ?? t`Search Token`}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        autoFocus={autoFocus ?? !isSmallerScreen}
        className={cx("block w-full rounded-28 border border-solid bg-gray-darkest placeholder-white-alpha-40", {
          "border-gray-light": isFocused,
          "border-gray-dark": !isFocused,
          "py-10 pl-40 pr-34 text-16": size === "m",
          "py-8 pl-36 pr-24 text-12 ": size === "s",
        })}
      />
      {value && (
        <button
          className={cx("group absolute bottom-0 right-0 top-0 flex items-center", {
            "pr-8": size === "m",
            "pr-4": size === "s",
          })}
          onClick={handleClear}
        >
          <div
            className={cx(
              "rounded-4 p-4 text-gray-lighter",
              "group-hover:bg-gray-darker group-hover:text-gray-lighter ",
              "group-active:bg-gray-darkest group-active:text-gray-lighter"
            )}
          >
            <CrossIconComponent
              className={cx("w-16", {
                "text-gray-lighter": !isFocused,
                "text-white": isFocused,
              })}
            />
          </div>
        </button>
      )}
    </div>
  );
}
